import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";

function safeJoinUrl(origin, path) {
  const o = String(origin || "").replace(/\/$/, "");
  const p = String(path || "/").startsWith("/") ? String(path || "/") : `/${path}`;
  return `${o}${p}`;
}

function getOrigin() {
  if (typeof window === "undefined") return "";
  return window.location?.origin || "";
}

/**
 * SEO helper (client-side). Note: for full SEO on SPAs, bots must execute JS.
 */
export default function Seo({
  title,
  description,
  path = "/",
  lang = "he",
  noIndex = false,
  ogType = "website",
  siteName = "Accss",
}) {
  const origin = getOrigin();
  const canonical = useMemo(() => (origin ? safeJoinUrl(origin, path) : ""), [origin, path]);

  const robots = noIndex
    ? "noindex,nofollow,noarchive,nosnippet"
    : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";

  // Lightweight JSON-LD (good for brand knowledge & rich results eligibility).
  const jsonLd = useMemo(() => {
    if (!canonical) return null;
    const url = canonical;
    const logo = origin ? safeJoinUrl(origin, "/favicon.svg") : undefined;

    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: siteName,
          url: origin || url,
          ...(logo ? { logo } : {}),
        },
        {
          "@type": "WebSite",
          name: siteName,
          url: origin || url,
          inLanguage: lang,
        },
        {
          "@type": "WebPage",
          name: title,
          url,
          description,
          inLanguage: lang,
        },
      ],
    };
  }, [canonical, description, lang, origin, siteName, title]);

  const ogLocale = lang === "en" ? "en_US" : "he_IL";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>

      {description ? <meta name="description" content={description} /> : null}
      <meta name="robots" content={robots} />

      {canonical ? <link rel="canonical" href={canonical} /> : null}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={ogLocale} />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:title" content={title} />
      {description ? <meta property="og:description" content={description} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      {description ? <meta name="twitter:description" content={description} /> : null}

      {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd)}</script> : null}
    </Helmet>
  );
}

